import { useState, useCallback, useEffect, useMemo } from 'react';
import { Card } from '@/components';
import {
  useNhtsaVinDecode,
  useWarranty,
  useMaintenance,
  useCommonRepairs,
  useMarketValue,
} from '@/hooks/useProApi';
import type {
  InjectedVehicleData,
  InjectedRepairSelection,
  RepairItem,
} from '@/types';

interface VehicleDataInjectionPanelProps {
  vin: string;
  reportType: 'emissions' | 'diagnostic' | 'pre_purchase' | 'estimate';
  onChange: (data: InjectedVehicleData | undefined) => void;
}

const MILEAGE_FILTERS = [5000, 15000, 30000, 60000, 90000] as const;

export function VehicleDataInjectionPanel({ vin, reportType, onChange }: VehicleDataInjectionPanelProps) {

  // --- Mileage state ---
  const [mileage, setMileage] = useState<number | undefined>();

  // --- NHTSA decode (auto-fetches when vin is valid) ---
  const nhtsaQuery = useNhtsaVinDecode(vin);
  const year = nhtsaQuery.data?.year?.toString() ?? null;
  const make = nhtsaQuery.data?.make ?? null;
  const model = nhtsaQuery.data?.model ?? null;

  // --- Section enabled states (controls whether hooks fetch) ---
  // Checking the checkbox sets enabled=true, triggering the hook to fetch
  const [warrantyEnabled, setWarrantyEnabled] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [repairsEnabled, setRepairsEnabled] = useState(false);
  const [marketValueEnabled, setMarketValueEnabled] = useState(false);

  // --- Data hooks ---
  const warrantyQuery = useWarranty(year, make, model, warrantyEnabled);
  const maintenanceQuery = useMaintenance(vin, maintenanceEnabled);
  const repairsQuery = useCommonRepairs(vin, repairsEnabled);
  const marketValueQuery = useMarketValue(vin, marketValueEnabled);

  // --- Derived data (memoized to prevent infinite re-render loops) ---
  const warrantyRaw = warrantyQuery.data as any;
  const warrantyData: Record<string, string> | null = warrantyRaw?.data?.warranty ?? null;
  const maintenanceData = useMemo(
    () => maintenanceQuery.data?.data?.maintenance ?? [],
    [maintenanceQuery.data]
  );
  const repairItems: RepairItem[] = useMemo(
    () => repairsQuery.data?.data?.repair ?? [],
    [repairsQuery.data]
  );
  const marketValueData = marketValueQuery.data?.data?.market_value?.market_value_data ?? null;

  // --- Section checked states ---
  const [warrantyChecked, setWarrantyChecked] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const [mileageFilter, setMileageFilter] = useState<number | 'all'>('all');
  const [repairsChecked, setRepairsChecked] = useState(false);
  const [selectedRepairs, setSelectedRepairs] = useState<Map<number, InjectedRepairSelection>>(new Map());
  const [repairSearch, setRepairSearch] = useState('');
  const [marketValueChecked, setMarketValueChecked] = useState(false);

  // --- Auto-check sections when data arrives ---
  useEffect(() => {
    if (warrantyData) setWarrantyChecked(true);
  }, [warrantyData]);

  useEffect(() => {
    if (maintenanceData.length > 0) setMaintenanceChecked(true);
  }, [maintenanceData]);

  useEffect(() => {
    if (repairItems.length > 0) setRepairsChecked(true);
  }, [repairItems]);

  useEffect(() => {
    if (marketValueData) setMarketValueChecked(true);
  }, [marketValueData]);

  // --- Checkbox handlers: checking triggers fetch, unchecking just unchecks ---
  function handleWarrantyToggle(checked: boolean) {
    setWarrantyChecked(checked);
    if (checked) setWarrantyEnabled(true);
  }

  function handleMaintenanceToggle(checked: boolean) {
    setMaintenanceChecked(checked);
    if (checked) setMaintenanceEnabled(true);
  }

  function handleRepairsToggle(checked: boolean) {
    setRepairsChecked(checked);
    if (checked) {
      setRepairsEnabled(true);
    } else {
      setSelectedRepairs(new Map());
    }
  }

  function handleMarketValueToggle(checked: boolean) {
    setMarketValueChecked(checked);
    if (checked) setMarketValueEnabled(true);
  }

  // --- Build injected data whenever selections change ---
  const buildInjectedData = useCallback((): InjectedVehicleData | undefined => {
    const result: InjectedVehicleData = {};
    let hasData = false;

    if (mileage) {
      result.mileage = mileage;
      hasData = true;
    }

    if (warrantyChecked && warrantyData) {
      result.warranty = warrantyData;
      hasData = true;
    }

    if (maintenanceChecked && maintenanceData.length > 0) {
      let filtered = maintenanceData;
      if (mileageFilter !== 'all') {
        filtered = maintenanceData.filter(
          (m) => (m.mileage?.miles ?? 0) <= mileageFilter
        );
      }
      result.maintenance = filtered.map((m) => ({
        miles: m.mileage?.miles ?? 0,
        km: m.mileage?.km,
        serviceItems: m.service_items ?? [],
      }));
      if (result.maintenance.length > 0) hasData = true;
    }

    if (repairsChecked && selectedRepairs.size > 0) {
      result.repairs = Array.from(selectedRepairs.values());
      hasData = true;
    }

    if (marketValueChecked && marketValueData && reportType === 'pre_purchase') {
      result.marketValue = marketValueData;
      hasData = true;
    }

    return hasData ? result : undefined;
  }, [mileage, warrantyChecked, warrantyData, maintenanceChecked, maintenanceData, mileageFilter, repairsChecked, selectedRepairs, marketValueChecked, marketValueData, reportType]);

  useEffect(() => {
    onChange(buildInjectedData());
  }, [buildInjectedData, onChange]);

  // --- Repair selection helpers ---
  function toggleRepair(idx: number, repair: RepairItem) {
    setSelectedRepairs((prev) => {
      const next = new Map(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        const indepCosts = repair.costs?.independent ?? [];
        const dealerCosts = repair.costs?.dealer ?? [];
        const indepAvg = indepCosts.reduce((s, c) => s + c.average, 0) / (indepCosts.length || 1);
        const dealerAvg = dealerCosts.reduce((s, c) => s + c.average, 0) / (dealerCosts.length || 1);
        next.set(idx, {
          title: repair.title,
          description: repair.description,
          priceSource: indepAvg > 0 ? 'independent' : 'dealer',
          dbIndependentAvg: Math.round(indepAvg * 100) / 100,
          dbDealerAvg: Math.round(dealerAvg * 100) / 100,
        });
      }
      return next;
    });
  }

  function updateRepairPrice(idx: number, field: keyof InjectedRepairSelection, value: any) {
    setSelectedRepairs((prev) => {
      const next = new Map(prev);
      const item = next.get(idx);
      if (item) {
        const updated = { ...item, [field]: value };
        if (field === 'customParts' || field === 'customLabor') {
          updated.customTotal = (updated.customParts ?? 0) + (updated.customLabor ?? 0);
        }
        next.set(idx, updated);
      }
      return next;
    });
  }

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
        <div className="flex-1">
          <span className="text-base font-semibold text-white">Include Data in Report</span>
          <p className="text-xs text-gray-300">Check items below and ARIA will weave them into your report</p>
        </div>
        {(warrantyChecked || maintenanceChecked || repairsChecked || marketValueChecked || mileage) && (
          <span className="text-sm font-medium bg-purple-500/20 text-purple-300 px-2.5 py-0.5 rounded-full">
            {[warrantyChecked, maintenanceChecked, repairsChecked, marketValueChecked].filter(Boolean).length + (mileage ? 1 : 0)} selected
          </span>
        )}
      </div>

      <div className="space-y-4">
          {/* Mileage Input — always visible */}
          <div className="p-3 bg-obdai-deep rounded-lg">
            <label className="flex items-center gap-3">
              <svg className="w-5 h-5 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <div className="flex-1">
                <span className="text-base text-white font-semibold">Vehicle Mileage</span>
                <p className="text-xs text-gray-400 mt-0.5">Current odometer reading for the report</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={mileage ?? ''}
                  onChange={(e) => setMileage(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g. 85000"
                  className="w-32 bg-black/30 text-white border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500/50 placeholder-gray-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-sm text-gray-400">mi</span>
              </div>
            </label>
          </div>

          {/* Warranty Section */}
          <div className="p-3 bg-obdai-deep rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={warrantyChecked}
                onChange={(e) => handleWarrantyToggle(e.target.checked)}
                className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/30"
              />
              <div className="flex-1">
                <span className="text-base text-white font-semibold">Add Manufacturer Warranty Details</span>
                {warrantyData ? (
                  <p className="text-sm text-gray-300 mt-0.5">
                    {Object.entries(warrantyData).slice(0, 3).map(([k, v]) =>
                      `${k.replace(/^Warranty - /, '')}: ${v}`
                    ).join(' | ')}
                    {Object.keys(warrantyData).length > 3 && ` +${Object.keys(warrantyData).length - 3} more`}
                  </p>
                ) : warrantyQuery.isLoading ? (
                  <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-2">
                    <span className="animate-spin inline-block w-3 h-3 border border-purple-400 border-t-transparent rounded-full" />
                    Loading warranty data...
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">Check to load warranty information</p>
                )}
              </div>
            </label>
          </div>

          {/* Maintenance Section */}
          <div className="p-3 bg-obdai-deep rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={maintenanceChecked}
                onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/30"
              />
              <div className="flex-1">
                <span className="text-base text-white font-semibold">Add Maintenance Schedule</span>
                {maintenanceData.length > 0 ? (
                  <p className="text-sm text-gray-300 mt-0.5">
                    {maintenanceData.length} service intervals loaded
                  </p>
                ) : maintenanceQuery.isLoading ? (
                  <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-2">
                    <span className="animate-spin inline-block w-3 h-3 border border-purple-400 border-t-transparent rounded-full" />
                    Loading maintenance data...
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">Check to load maintenance schedule</p>
                )}
              </div>
            </label>
            {maintenanceChecked && maintenanceData.length > 0 && (
              <div className="mt-2 ml-7 flex flex-wrap gap-1.5">
                {MILEAGE_FILTERS.map((miles) => (
                  <button
                    key={miles}
                    onClick={() => setMileageFilter(mileageFilter === miles ? 'all' : miles)}
                    className={`px-3 py-1 text-sm rounded-full transition ${
                      mileageFilter === miles
                        ? 'bg-purple-500/30 text-purple-300 font-medium border border-purple-500/50'
                        : 'bg-gray-800 text-gray-300 border border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {miles >= 1000 ? `${miles / 1000}K` : miles} mi
                  </button>
                ))}
                <button
                  onClick={() => setMileageFilter('all')}
                  className={`px-3 py-1 text-sm rounded-full transition ${
                    mileageFilter === 'all'
                      ? 'bg-purple-500/30 text-purple-300 font-medium border border-purple-500/50'
                      : 'bg-gray-800 text-gray-300 border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  All
                </button>
              </div>
            )}
          </div>

          {/* Repairs Section */}
          <div className="p-3 bg-obdai-deep rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={repairsChecked}
                onChange={(e) => handleRepairsToggle(e.target.checked)}
                className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/30"
              />
              <div className="flex-1">
                <span className="text-base text-white font-semibold">Add Common Repairs</span>
                {repairItems.length > 0 ? (
                  <p className="text-sm text-gray-300 mt-0.5">
                    {repairItems.length} repairs available{selectedRepairs.size > 0 ? ` (${selectedRepairs.size} selected)` : ''}
                  </p>
                ) : repairsQuery.isLoading ? (
                  <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-2">
                    <span className="animate-spin inline-block w-3 h-3 border border-purple-400 border-t-transparent rounded-full" />
                    Loading repair data...
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">Check to load common repairs</p>
                )}
              </div>
            </label>
            {repairsChecked && repairItems.length > 0 && (() => {
              let grandTotal = 0;
              selectedRepairs.forEach((r) => {
                if (r.priceSource === 'custom') {
                  grandTotal += r.customTotal ?? 0;
                } else if (r.priceSource === 'independent') {
                  grandTotal += r.dbIndependentAvg ?? 0;
                } else {
                  grandTotal += r.dbDealerAvg ?? 0;
                }
              });

              const searchLower = repairSearch.toLowerCase();
              const filteredRepairs = searchLower
                ? repairItems.map((r, i) => ({ repair: r, idx: i })).filter(({ repair }) => repair.title.toLowerCase().includes(searchLower))
                : repairItems.map((r, i) => ({ repair: r, idx: i }));

              return (
                <>
                  {/* Search input */}
                  <div className="mt-2 ml-7">
                    <input
                      type="text"
                      value={repairSearch}
                      onChange={(e) => setRepairSearch(e.target.value)}
                      placeholder="Search repairs..."
                      className="w-full bg-black/30 text-gray-200 border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500/50 placeholder-gray-500"
                    />
                  </div>
                  <div className="mt-2 ml-7 space-y-2 max-h-[240px] overflow-y-auto pr-1">
                    {filteredRepairs.map(({ repair, idx }) => {
                      const sel = selectedRepairs.get(idx);
                      const hasIndepData = (sel?.dbIndependentAvg ?? 0) > 0;
                      return (
                        <div key={idx} className="p-2 bg-black/20 rounded-lg">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!sel}
                              onChange={() => toggleRepair(idx, repair)}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/30"
                            />
                            <span className="text-sm font-medium text-white">{repair.title}</span>
                          </label>
                          {sel && (
                            <div className="mt-2 ml-6 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                              {hasIndepData && (
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`price-${idx}`}
                                    checked={sel.priceSource === 'independent'}
                                    onChange={() => updateRepairPrice(idx, 'priceSource', 'independent')}
                                    className="w-3 h-3 text-purple-500"
                                  />
                                  <span className="text-sm text-gray-200">
                                    Avg Independent <span className="text-green-400 font-medium">${sel.dbIndependentAvg?.toFixed(0)}</span>
                                  </span>
                                </label>
                              )}
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`price-${idx}`}
                                  checked={sel.priceSource === 'dealer'}
                                  onChange={() => updateRepairPrice(idx, 'priceSource', 'dealer')}
                                  className="w-3 h-3 text-purple-500"
                                />
                                <span className="text-sm text-gray-200">
                                  Avg Dealer <span className="text-yellow-400 font-medium">${sel.dbDealerAvg?.toFixed(0)}</span>
                                </span>
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`price-${idx}`}
                                  checked={sel.priceSource === 'custom'}
                                  onChange={() => updateRepairPrice(idx, 'priceSource', 'custom')}
                                  className="w-3 h-3 text-purple-500"
                                />
                                <span className="text-sm text-gray-200">My Price</span>
                              </label>
                              {sel.priceSource === 'custom' && (
                                <div className="flex items-center gap-2 w-full ml-0 mt-1">
                                  <label className="text-sm text-gray-400">Parts $</label>
                                  <input
                                    type="number"
                                    value={sel.customParts ?? ''}
                                    onChange={(e) => updateRepairPrice(idx, 'customParts', parseFloat(e.target.value) || 0)}
                                    className="w-20 bg-black/40 border border-gray-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-purple-500/50"
                                  />
                                  <label className="text-sm text-gray-400">Labor $</label>
                                  <input
                                    type="number"
                                    value={sel.customLabor ?? ''}
                                    onChange={(e) => updateRepairPrice(idx, 'customLabor', parseFloat(e.target.value) || 0)}
                                    className="w-20 bg-black/40 border border-gray-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-purple-500/50"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {filteredRepairs.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-2">No repairs matching "{repairSearch}"</p>
                    )}
                  </div>
                  {selectedRepairs.size > 0 && (
                    <div className="mt-2 ml-7 flex items-center gap-4 px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                      <span className="text-sm font-semibold text-purple-300">{selectedRepairs.size} repair{selectedRepairs.size !== 1 ? 's' : ''}</span>
                      <span className="text-sm text-gray-200">
                        Estimated Total: <span className="text-white font-semibold">${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Market Value Section (pre-purchase only) */}
          {reportType === 'pre_purchase' && (
            <div className="p-3 bg-obdai-deep rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketValueChecked}
                  onChange={(e) => handleMarketValueToggle(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/30"
                />
                <div className="flex-1">
                  <span className="text-base text-white font-semibold">Add Market Value</span>
                  {marketValueData ? (
                    <p className="text-sm text-gray-300 mt-0.5">
                      {marketValueData.length} trim{marketValueData.length !== 1 ? 's' : ''} with pricing data
                    </p>
                  ) : marketValueQuery.isLoading ? (
                    <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-2">
                      <span className="animate-spin inline-block w-3 h-3 border border-purple-400 border-t-transparent rounded-full" />
                      Loading market value data...
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-0.5">Check to load market value data</p>
                  )}
                </div>
              </label>
            </div>
          )}
        </div>
    </Card>
  );
}
